import { Injectable, inject } from '@angular/core';
import {
  Action,
  Selector,
  State,
  type StateContext,
  StateToken,
  Store,
} from '@ngxs/store';
import { uniqBy } from 'lodash-es';
import { Observable } from 'rxjs';
import { finalize, mergeMap, tap } from 'rxjs/operators';

import { COMMENT_DEFAULT_SORT_OPTIONS } from '@plugins/client/post/post-comments/constants';
import type {
  FullAnswerDto,
  QuestionDto,
  StatusLabels,
} from '@plugins/faq-lib/types';
import { SortType } from '@plugins/shared/types/sort';

import { AnswerStateModel } from '../../../types';
import { DEFAULT_LIKE_DTO } from '../../constants';
import { isTopAnswer, refactorAnswerComments } from '../../helpers';
import { AnswerService } from '../../services/answer.service';
import {
  CommentsLoaded,
  MoreCommentsLoaded,
  SubscribedToAnswer,
} from '../comment/comment.actions';
import { InstanceState } from '../instance/instance.state';
import {
  AnswerCreated,
  AnswerDeleted,
  ChangeQuestionStatus,
} from '../question/question.actions';

import {
  AnswersLoaded,
  AnswerSubscribe,
  AnswerUnsubscribe,
  ChangeAcceptAnswer,
  ChangeAnswersSort,
  ChangeAnswerVote,
  CreateAnswer,
  DeleteAnswer,
  LoadAnswers,
  LoadMoreAnswers,
  UpdateAnswer,
} from './answer.actions';

const defaultSort = COMMENT_DEFAULT_SORT_OPTIONS[0]?.value;

const ANSWER_STATE_TOKEN = new StateToken<AnswerStateModel>('answer');

@State<AnswerStateModel>({
  name: ANSWER_STATE_TOKEN,
  defaults: {
    answers: [],
    total: 0,
    loading: false,
    loadingMore: false,
    sort: defaultSort,
  },
})
@Injectable()
export class AnswerState {
  private readonly answerService = inject(AnswerService);
  private readonly store = inject(Store);

  @Selector()
  static answers(state: AnswerStateModel) {
    return state.answers;
  }

  @Selector()
  static acceptedAnswers(state: AnswerStateModel) {
    return state.answers.filter((a) => a.correct);
  }

  @Selector()
  static allLoaded(state: AnswerStateModel) {
    return state.answers.length === state.total;
  }

  @Selector()
  static loading(state: AnswerStateModel) {
    return state.loading;
  }

  @Selector()
  static loadingMore(state: AnswerStateModel) {
    return state.loadingMore;
  }

  @Selector()
  static total(state: AnswerStateModel) {
    return state.total;
  }

  @Selector()
  static sortType(state: AnswerStateModel) {
    return state.sort;
  }

  @Action(LoadAnswers)
  loadAnswers(
    { patchState, getState, dispatch }: StateContext<AnswerStateModel>,
    { question, resetSort, skip, take }: LoadAnswers,
  ) {
    const sort = resetSort ? defaultSort : getState().sort;
    patchState({
      loading: true,
      loadingMore: false,
      sort,
    });
    return this.answerService.load(question.id, sort, skip, take).pipe(
      mergeMap((response) => {
        const { answers, comments } = refactorAnswerComments([
          ...response.topAnswers,
          ...response.answers,
        ]);
        const total = response.count;
        patchState({
          answers,
          total,
          loading: false,
        });
        this.checkQuestionStatusChange(question, response.topAnswers, dispatch);
        return dispatch(new CommentsLoaded(comments));
      }),
    );
  }

  @Action(AnswersLoaded)
  answersLoaded(
    { patchState }: StateContext<AnswerStateModel>,
    { answers, total }: AnswersLoaded,
  ) {
    patchState({
      answers,
      total,
    });
  }

  @Action(LoadMoreAnswers)
  loadMoreAnswers(
    { getState, patchState, dispatch }: StateContext<AnswerStateModel>,
    { question }: LoadMoreAnswers,
  ) {
    patchState({ loadingMore: true });
    const { answers: currentAnswers, sort } = getState();
    const { potentialMinVotes } = this.store.selectSnapshot(
      InstanceState.settings,
    );
    let topAnswers = 0;
    for (const answer of currentAnswers) {
      if (!isTopAnswer(answer, potentialMinVotes)) {
        break;
      }
      topAnswers++;
    }
    const skip = currentAnswers.length - topAnswers;
    return this.answerService.load(question.id, sort, skip).pipe(
      mergeMap((res) => {
        const { comments } = refactorAnswerComments([
          ...res.topAnswers,
          ...res.answers,
        ]);
        return dispatch(new MoreCommentsLoaded(comments)).pipe(
          finalize(() => {
            const oldAnswers = [
              ...currentAnswers
                .slice(0, topAnswers)
                .map((a) => ({ ...a, correct: false })),
              ...currentAnswers.slice(topAnswers),
            ];
            patchState({
              answers: uniqBy(
                [...res.topAnswers, ...oldAnswers, ...res.answers],
                'id',
              ),
              loadingMore: false,
              total: res.count,
            });
            this.checkQuestionStatusChange(question, res.topAnswers, dispatch);
          }),
        );
      }),
    );
  }

  @Action(CreateAnswer)
  createAnswer(
    { getState, patchState, dispatch }: StateContext<AnswerStateModel>,
    { answer, questionId }: CreateAnswer,
  ) {
    return this.answerService.create(answer, questionId).pipe(
      tap(({ answer, questionSubscription }) => {
        const createdAnswer = { ...answer, ...DEFAULT_LIKE_DTO };
        const { answers, total, sort } = getState();
        const updatedAnswers = [...answers];
        if (sort === SortType.NEW_OLD) {
          const { potentialMinVotes } = this.store.selectSnapshot(
            InstanceState.settings,
          );
          const index = answers.findIndex(
            (a) => !a.correct && a.totalUpVotes <= potentialMinVotes,
          );
          if (index !== -1) {
            updatedAnswers.splice(index, 0, createdAnswer);
          } else if (answers.length === total) {
            updatedAnswers.push(createdAnswer);
          }
        } else if (sort === SortType.OLD_NEW && answers.length === total) {
          updatedAnswers.push(createdAnswer);
        } else if (sort === SortType.POPULAR || sort === SortType.UNPOPULAR) {
          const { potentialMinVotes } = this.store.selectSnapshot(
            InstanceState.settings,
          );
          const index = answers.findIndex((a) => {
            const isNotPotentiallyCorrect = a.totalUpVotes <= potentialMinVotes;
            const rightVoteAmount =
              sort === SortType.POPULAR
                ? a.totalUpVotes - a.totalDownVotes <= 0
                : a.totalUpVotes - a.totalDownVotes >= 0;
            return !a.correct && isNotPotentiallyCorrect && rightVoteAmount;
          });
          if (index !== -1) {
            updatedAnswers.splice(index, 0, createdAnswer);
          } else if (answers.length === total) {
            updatedAnswers.push(createdAnswer);
          }
        }
        patchState({
          answers: updatedAnswers,
          total: total + 1,
        });
        dispatch(new AnswerCreated(questionId, questionSubscription));
      }),
    );
  }

  @Action(UpdateAnswer)
  updateAnswer(
    { getState, patchState }: StateContext<AnswerStateModel>,
    { answer }: UpdateAnswer,
  ) {
    return this.answerService.update(answer).pipe(
      tap((updatedAnswer) => {
        const { answers } = getState();
        patchState({
          answers: answers.map((a) =>
            a.id === updatedAnswer.id ? { ...a, ...updatedAnswer } : a,
          ),
        });
      }),
    );
  }

  @Action(DeleteAnswer)
  deleteAnswer(
    { getState, patchState, dispatch }: StateContext<AnswerStateModel>,
    { answerId }: DeleteAnswer,
  ) {
    return this.answerService.delete(answerId).pipe(
      tap(({ question }) => {
        const { answers, total } = getState();
        patchState({
          answers: answers.filter((a) => a.id !== answerId),
          total: total - 1,
        });
        dispatch(new AnswerDeleted(question));
      }),
    );
  }

  @Action(ChangeAnswerVote)
  changeVote(
    { getState, patchState, dispatch }: StateContext<AnswerStateModel>,
    { answerId, vote, voted, questionStatus }: ChangeAnswerVote,
  ) {
    return this.answerService.changeVote(answerId, voted).pipe(
      tap(({ question }) => {
        const { answers: storeAnswers } = getState();
        const answers = storeAnswers.map((a) => {
          if (a.id !== answerId) {
            return a;
          }
          const updatedAnswer = {
            ...a,
            totalUpVotes:
              a.totalUpVotes + (voted && vote === 1 ? 1 : a.upvoted ? -1 : 0),
            totalDownVotes:
              a.totalDownVotes +
              (voted && vote === -1 ? 1 : a.downvoted ? -1 : 0),
            upvoted: voted === 1 ? 1 : 0,
            downvoted: voted === -1 ? 1 : 0,
            voted: voted ? 1 : 0,
          };
          return updatedAnswer;
        });
        patchState({
          answers,
        });
        if (questionStatus !== question.status) {
          dispatch(new ChangeQuestionStatus(question.id, question.status));
        }
      }),
    );
  }

  @Action(ChangeAcceptAnswer)
  changeAnswerAccept(
    { getState, patchState, dispatch }: StateContext<AnswerStateModel>,
    { answerId, questionStatus }: ChangeAcceptAnswer,
  ) {
    return this.answerService.changeAccept(answerId).pipe(
      tap(({ question, answer }) => {
        const { answers: storeAnswers } = getState();
        const answers = storeAnswers.map((a) =>
          a.id === answer.id ? { ...a, correct: answer.correct } : a,
        );
        patchState({ answers });
        const { status, id } = question;
        if (questionStatus !== status) {
          dispatch(new ChangeQuestionStatus(id, status));
        }
      }),
    );
  }

  @Action(ChangeAnswersSort)
  changeFilter(
    { getState, patchState, dispatch }: StateContext<AnswerStateModel>,
    { sort, question, skip, take }: ChangeAnswersSort,
  ) {
    const currentSort = getState().sort;
    if (sort !== currentSort) {
      patchState({ sort });
      return dispatch(new LoadAnswers(question, false, skip, take));
    }
  }

  private checkQuestionStatusChange(
    question: QuestionDto,
    topAnswers: FullAnswerDto[],
    dispatch: (action: unknown) => Observable<void>,
  ) {
    let newStatus: StatusLabels = 'label_active';
    if (topAnswers.length) {
      const correctAnswer = topAnswers.find((answer) => answer.correct);
      newStatus = correctAnswer
        ? 'label_answered'
        : 'label_potentially_answered';
    }

    if (question.status !== newStatus) {
      dispatch(new ChangeQuestionStatus(question.id, newStatus));
    }
  }

  @Action(AnswerSubscribe)
  questionSubscribe(
    { getState, patchState }: StateContext<AnswerStateModel>,
    { answerId }: AnswerSubscribe,
  ) {
    return this.answerService.subscribe(answerId).pipe(
      tap((subscription) => {
        const { answers } = getState();
        patchState({
          answers: answers.map((a) =>
            a.id === answerId ? { ...a, subscription } : a,
          ),
        });
      }),
    );
  }

  @Action(AnswerUnsubscribe)
  questionUnsubscribe(
    { getState, patchState }: StateContext<AnswerStateModel>,
    { answerId }: AnswerUnsubscribe,
  ) {
    return this.answerService.unsubscribe(answerId).pipe(
      tap((subscription) => {
        const { answers } = getState();
        patchState({
          answers: answers.map((a) =>
            a.id === answerId ? { ...a, subscription } : a,
          ),
        });
      }),
    );
  }

  @Action(SubscribedToAnswer)
  subscribedToAnswer(
    { getState, patchState }: StateContext<AnswerStateModel>,
    { subscription }: SubscribedToAnswer,
  ) {
    const { answers } = getState();
    patchState({
      answers: answers.map((a) =>
        a.id === subscription.entityId ? { ...a, subscription } : a,
      ),
    });
  }
}
